import type { SeasonPhase } from "./crowd-prediction";

export type SkiSeason = {
  label: string;
  startDate: string;
  endDate: string;
  startYear: number;
  endYear: number;
};

export type SurfSeason = {
  label: string;
  startDate: string;
  endDate: string;
  year: number;
};

export function getSkiSeason(date: Date): SkiSeason {
  const month = date.getUTCMonth() + 1;
  const year = date.getUTCFullYear();
  const startYear = month >= 11 ? year : year - 1;
  const endYear = startYear + 1;
  return {
    label: `${startYear}-${String(endYear).slice(2)}`,
    startDate: `${startYear}-11-01`,
    endDate: `${endYear}-04-30`,
    startYear,
    endYear,
  };
}

export function getNextSkiSeason(date: Date): SkiSeason {
  const current = getSkiSeason(date);
  return getSkiSeason(
    new Date(Date.UTC(current.startYear + 1, 10, 15))
  );
}

export function getSouthernSkiSeason(date: Date): SkiSeason {
  const year = date.getUTCFullYear();
  const month = date.getUTCMonth() + 1;
  const seasonYear = month >= 6 ? year : year - 1;
  return {
    label: String(seasonYear),
    startDate: `${seasonYear}-06-01`,
    endDate: `${seasonYear}-10-31`,
    startYear: seasonYear,
    endYear: seasonYear,
  };
}

export function getNextSouthernSkiSeason(date: Date): SkiSeason {
  const current = getSouthernSkiSeason(date);
  return getSouthernSkiSeason(
    new Date(Date.UTC(current.startYear + 1, 5, 15))
  );
}

export function isSouthernSkiInSeason(date: Date): boolean {
  const month = date.getUTCMonth() + 1;
  return month >= 6 && month <= 10;
}

export function getPreviousSkiSeason(date: Date): SkiSeason {
  const current = getSkiSeason(date);
  return getSkiSeason(
    new Date(Date.UTC(current.startYear - 1, 10, 15))
  );
}

export function isSkiInSeason(date: Date): boolean {
  const month = date.getUTCMonth() + 1;
  return month >= 11 || month <= 4;
}

export function getSurfSeason(date: Date): SurfSeason {
  const year = date.getUTCFullYear();
  return {
    label: String(year),
    startDate: `${year}-01-01`,
    endDate: `${year}-12-31`,
    year,
  };
}

export function getNextSurfSeason(date: Date): SurfSeason {
  return getSurfSeason(
    new Date(Date.UTC(date.getUTCFullYear() + 1, 0, 15))
  );
}

export function getPreviousSurfSeason(date: Date): SurfSeason {
  return getSurfSeason(
    new Date(Date.UTC(date.getUTCFullYear() - 1, 0, 15))
  );
}

export function hemisphereFromLatitude(latitude: number): "north" | "south" {
  return latitude < 0 ? "south" : "north";
}

/**
 * Planning tools (Best Time to Go): forward-looking defaults.
 * Ski in-season: current season. Off-season: next season.
 * Surf: always current calendar year.
 */
export function getPlanningSeasonSki(
  date: Date,
  hemisphere: "north" | "south" = "north"
): {
  primary: SkiSeason;
  alternate: SkiSeason;
} {
  if (hemisphere === "south") {
    if (isSouthernSkiInSeason(date)) {
      return {
        primary: getSouthernSkiSeason(date),
        alternate: getNextSouthernSkiSeason(date),
      };
    }
    return {
      primary: getNextSouthernSkiSeason(date),
      alternate: getSouthernSkiSeason(date),
    };
  }

  if (isSkiInSeason(date)) {
    return {
      primary: getSkiSeason(date),
      alternate: getNextSkiSeason(date),
    };
  }
  return {
    primary: getNextSkiSeason(date),
    alternate: getSkiSeason(date),
  };
}

export function getPlanningSeasonSurf(date: Date): {
  primary: SurfSeason;
  alternate: SurfSeason;
} {
  return {
    primary: getSurfSeason(date),
    alternate: getNextSurfSeason(date),
  };
}

export function generateWeekStarts(
  startDate: string,
  endDate: string
): { weekStart: string; weekEnd: string }[] {
  const weeks: { weekStart: string; weekEnd: string }[] = [];
  const end = new Date(endDate + "T00:00:00Z");

  const cursor = new Date(startDate + "T00:00:00Z");
  const day = cursor.getUTCDay();
  const daysToMonday = day === 0 ? 1 : day === 1 ? 0 : 8 - day;
  cursor.setUTCDate(cursor.getUTCDate() + daysToMonday);

  while (cursor <= end) {
    const weekStart = cursor.toISOString().slice(0, 10);
    const weekEndDate = new Date(cursor);
    weekEndDate.setUTCDate(weekEndDate.getUTCDate() + 6);
    const weekEnd = weekEndDate.toISOString().slice(0, 10);
    weeks.push({ weekStart, weekEnd });
    cursor.setUTCDate(cursor.getUTCDate() + 7);
  }
  return weeks;
}

export function getWeekOfYear(dateStr: string): number {
  const d = new Date(dateStr + "T00:00:00Z");
  d.setUTCDate(d.getUTCDate() + 4 - (d.getUTCDay() || 7));
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil(((d.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
}

export function getSeasonPhase(
  date: string,
  seasonStart: string,
  seasonEnd: string
): SeasonPhase {
  const d = new Date(date + "T00:00:00Z").getTime();
  const start = new Date(seasonStart + "T00:00:00Z").getTime();
  const end = new Date(seasonEnd + "T00:00:00Z").getTime();
  const totalDays = (end - start) / 86_400_000;
  const elapsed = (d - start) / 86_400_000;
  const pct = Math.max(0, Math.min(1, elapsed / totalDays));

  if (pct < 0.15) return "early";
  if (pct < 0.35) return "shoulder";
  if (pct < 0.75) return "peak";
  return "late";
}
